import { GridApi, IDatasource, IGetRowsParams, SortModelItem } from 'ag-grid-community';
import dayjs from 'dayjs';

import { AgGridFilterModel } from '@customTypes/ag-grid';
import { QUERY_PARAM_KEYS, SORT_DIRECTIONS, SortDirection } from '@customTypes/common';
import type { DisplayableOrderStatus, ResultsType } from '@customTypes/order';
import { ExtendedRequisitionListResultItem, PaginatedRequisitionsResponse } from '@customTypes/requisition';
import { ORDER_STATUS_CONFIG, RESULT_TYPE_CONFIG } from '@helpers/order';
import { getTimelineStatusLabel } from '@helpers/requisition';
import { fetchPaginatedRequisitions } from '@store/actions';
import type { RequisitionTableColumnId } from './columns';

type FilterColumn = Extract<
  RequisitionTableColumnId,
  | 'clinician'
  | 'external_mrn'
  | 'order_date'
  | 'patient'
  | 'requisition_id'
  | 'result'
  | 'sample_received_at'
  | 'status'
>;

type FilterQueryParam =
  | 'clinician_name'
  | 'external_mrn'
  | 'order_date'
  | 'patient_name'
  | 'requisition_id'
  | 'result'
  | 'sample_received_at'
  | 'status';

type FilterParams = Partial<Record<FilterQueryParam, string>>;

const FILTER_COLUMN_QUERY_PARAMS: Record<FilterColumn, FilterQueryParam> = {
  clinician: 'clinician_name',
  external_mrn: 'external_mrn',
  order_date: 'order_date',
  patient: 'patient_name',
  requisition_id: 'requisition_id',
  result: 'result',
  sample_received_at: 'sample_received_at',
  status: 'status',
};

type SortColumn = Extract<
  RequisitionTableColumnId,
  'clinician' | 'external_mrn' | 'order_date' | 'patient' | 'requisition_id' | 'sample_received_at'
>;

type SortField =
  | 'clinician_name'
  | 'external_mrn'
  | 'order_date'
  | 'patient_name'
  | 'requisition_id'
  | 'sample_received_at';

const SORT_COLUMN_QUERY_PARAMS: Record<SortColumn, SortField> = {
  clinician: 'clinician_name',
  external_mrn: 'external_mrn',
  order_date: 'order_date',
  patient: 'patient_name',
  requisition_id: 'requisition_id',
  sample_received_at: 'sample_received_at',
};

interface SortParam {
  direction: SortDirection;
  field: SortField;
}

export class RequisitionTableDataSource implements IDatasource {
  // eslint-disable-next-line no-useless-constructor
  public constructor(
    private readonly gridApi: GridApi,
    private readonly pageSize: number,
    private readonly onError?: (error: string) => void,
  ) {}

  async getRows(params: IGetRowsParams): Promise<void> {
    const gridApi = this.gridApi;

    gridApi.showLoadingOverlay();
    const queryParams = this.buildQueryParams(params);

    let apiResponse: PaginatedRequisitionsResponse;
    try {
      apiResponse = await fetchPaginatedRequisitions(queryParams);
    } catch (err) {
      gridApi.hideOverlay();
      params.failCallback();
      this.onError?.('Server Error');
      return;
    }

    const extendedRequisitions = apiResponse.results.map<ExtendedRequisitionListResultItem>(requisition => ({
      ...requisition,
      display_status: getTimelineStatusLabel(requisition),
    }));

    params.successCallback(extendedRequisitions, apiResponse.count);
  }

  buildQueryParams(params: IGetRowsParams): URLSearchParams {
    const filterParams = this.parseFilterParams(params.filterModel);
    const sortParam = this.parseSortParam(params.sortModel);
    const page = this.getPage(params.startRow);

    const queryParams = new URLSearchParams({
      ...filterParams,
      [QUERY_PARAM_KEYS.page]: `${page}`,
      [QUERY_PARAM_KEYS.pageSize]: `${this.pageSize}`,
    });

    if (sortParam) {
      queryParams.append(QUERY_PARAM_KEYS.sortField, sortParam.field);
      queryParams.append(QUERY_PARAM_KEYS.sortDirection, sortParam.direction);
    }

    return queryParams;
  }

  getPage(startRow: number): number {
    return 1 + Math.floor(startRow / this.pageSize);
  }

  isFilterColumn(columnId: string): columnId is FilterColumn {
    return columnId in FILTER_COLUMN_QUERY_PARAMS;
  }

  isSortColumn(columnId: string): columnId is SortColumn {
    return columnId in SORT_COLUMN_QUERY_PARAMS;
  }

  parseSortParam(sortModel: SortModelItem[]): SortParam | undefined {
    const [sortItem] = sortModel;

    if (!sortItem) {
      return;
    }

    if (this.isSortColumn(sortItem.colId)) {
      return {
        direction: sortItem.sort === 'asc' ? SORT_DIRECTIONS.ascending : SORT_DIRECTIONS.descending,
        field: SORT_COLUMN_QUERY_PARAMS[sortItem.colId],
      };
    }
  }

  parseFilterParams(filterModel: AgGridFilterModel): FilterParams {
    const filterParams: FilterParams = {};

    for (const [field, item] of Object.entries(filterModel)) {
      if (!this.isFilterColumn(field)) {
        continue;
      }

      const queryKey = FILTER_COLUMN_QUERY_PARAMS[field];

      let filterValue = '';

      if (item.filterType === 'date' && item.dateFrom) {
        filterValue = dayjs(item.dateFrom).format('YYYY-MM-DD');
      } else if (item.filterType === 'text' && item.filter) {
        filterValue = item.filter;
      } else {
        continue;
      }

      if (field === 'result') {
        if (filterValue in RESULT_TYPE_CONFIG) {
          const mappedResults = RESULT_TYPE_CONFIG[filterValue as ResultsType].findings;

          if (mappedResults.length) {
            filterParams[queryKey] = mappedResults.join(',');
          }
        }
      } else if (field === 'status') {
        if (filterValue in ORDER_STATUS_CONFIG) {
          const mappedValue = ORDER_STATUS_CONFIG[filterValue as DisplayableOrderStatus].filterValue;
          filterParams[queryKey] = mappedValue;
        }
      } else {
        filterParams[queryKey] = filterValue;
      }
    }

    return filterParams;
  }
}
